import { useMemo } from 'react';
import { useAppTheme } from '@/contexts/AppThemeContext';
import type { EChartsOption } from 'echarts';

/**
 * Maps app theme to ECharts option tokens (axis, grid, tooltip, legend, data labels).
 * Prefers CSS variable–driven values; falls back to safe defaults for ECharts.
 */
export function useChartTheme(): { theme: 'light' | 'dark'; optionPatches: Partial<EChartsOption> & { _seriesLabelColor?: string } } {
  const { theme } = useAppTheme();
  const isDark = theme === 'dark';

  const optionPatches = useMemo<Partial<EChartsOption> & { _seriesLabelColor?: string }>(() => {
    // Dark: use brighter gray for labels/legends so they remain readable on dark backgrounds
    const textColor = isDark ? '#D1D5DB' : '#6B7280';
    const axisLineColor = isDark ? '#374151' : '#E5E7EB';
    const splitLineColor = isDark ? '#374151' : '#E5E7EB';
    const backgroundColor = 'transparent';

    return {
      backgroundColor,
      textStyle: {
        color: textColor,
        fontSize: 12,
      },
      grid: {
        top: 16,
        right: 16,
        bottom: 64,
        left: 56,
        containLabel: true,
      },
      xAxis: {
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: textColor, margin: 12, hideOverlap: true },
        splitLine: { show: false },
        nameTextStyle: { fontSize: 12, fontWeight: 600, color: textColor },
      },
      yAxis: {
        axisLine: { show: false },
        axisLabel: { color: textColor, margin: 12, hideOverlap: true },
        splitLine: { lineStyle: { color: splitLineColor, type: 'dashed' } },
        nameTextStyle: { fontSize: 12, fontWeight: 600, color: textColor },
      },
      tooltip: {
        backgroundColor: isDark ? '#111827' : '#FFFFFF',
        borderColor: isDark ? '#374151' : '#E5E7EB',
        textStyle: { color: isDark ? '#F3F4F6' : '#111827' },
      },
      legend: {
        textStyle: { color: textColor, fontSize: 12 },
        pageTextStyle: { color: textColor },
      },
      _seriesLabelColor: textColor,
    };
  }, [isDark]);

  return { theme: theme, optionPatches };
}
